import { useState } from "react";
import { orgApi } from "../utils/apiV2";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { 
  Plus, Trash2, Edit, Users, User,
  AlertCircle, CheckSquare, Mail, Phone,
  Loader2, Check, LayoutGrid, List, ArrowUpRight, Shield
} from "lucide-react";
import { toast } from "sonner";
import Pagination from "./Pagination";
import AddTeamMemberModal from "./AddTeamMemberModal";
import TabPageLayout from './TabPageLayout';
import MassActionToolbar from './MassActionToolbar';
import EmptyState from './EmptyState';
import { PrimaryButton, SecondaryButton, LayoutActionButton } from './Buttons';
import { useRoles } from '../hooks/useData';
import RoleSelector from './RoleSelector';
import ModalTabSelector from './ModalTabSelector';
import DeleteConfirmModal from './DeleteConfirmModal';
import StandardModal from './StandardModal';
import ResourceCard from './ResourceCard';

const TeamMembersTab = ({ teamMembers, onRefresh, isLoading: propsLoading, isError: propsError }) => {
  const [open, setOpen] = useState(false);
  const [editTab, setEditTab] = useState("basic");
  const [viewMode, setViewMode] = useState("card");
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: "" });
  const [editingTeamMember, setEditingTeamMember] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  
  // Mass action states
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  
  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  
  // Role management with useSWR
  const { roles } = useRoles();
  const [selectedRoleId, setSelectedRoleId] = useState("");

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Schedule/priority are no longer edited in the UI but are preserved on save
  const [recurringAvailability, setRecurringAvailability] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [schedulerWeight, setSchedulerWeight] = useState(3);
  const [formHasChanges, setFormHasChanges] = useState(false);


  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    setSelectedTeamMembers([]);
  };
  
  const toggleTeamMemberSelection = (teamMemberId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(teamMemberId) 
        ? prev.filter(id => id !== teamMemberId)
        : [...prev, teamMemberId]
    );
  };
  
  const selectAllTeamMembers = () => {
    setSelectedTeamMembers(paginatedTeamMembers.map(tm => tm.id));
  };
  
  const deselectAllTeamMembers = () => {
    setSelectedTeamMembers([]);
  };
  
  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const deletePromises = selectedTeamMembers.map(teamMemberId =>
        orgApi.deleteTeamMember(teamMemberId)
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedTeamMembers.length} team member(s) deleted successfully`);
      setSelectedTeamMembers([]);
      setMassActionMode(false);
      setBulkDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      console.error("Error deleting team members:", error);
      toast.error("Failed to delete some team members");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleNameChange = (value) => {
    setName(value);
    if (!usernameManuallyEdited && !editingTeamMember) {
      const generatedUsername = value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 20);
      setUsername(generatedUsername);
    }
  };

  const goToProfileSettings = () => {
    setOpen(false);
    setEditingTeamMember(null);
    window.dispatchEvent(new CustomEvent('openSettings', { detail: { tab: 'settings-profile' } }));
  };

  const handleEditClick = (teamMember) => {
    setEditingTeamMember(teamMember);
    setEditTab("basic");
    setName(teamMember.name);
    setEmail(teamMember.email || "");
    setPhone(teamMember.phone || "");
    setUsername(teamMember.username || "");
    setPassword("");
    setRecurringAvailability(teamMember.recurringAvailability || []);
    setUnavailableDates(teamMember.unavailableDates || []);
    setSchedulerWeight(teamMember.schedulerWeight || 3);
    setSelectedRoleId(teamMember.roleId || "");
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter team member name");
      return;
    }

    if (!editingTeamMember && !selectedRoleId) {
      toast.error("Please select a role for this team member");
      return;
    }

    if (!editingTeamMember && (!username || !password)) {
      toast.error("Please enter username and password for new team member");
      return;
    }

    setIsSaving(true);
    try {
      const selectedRole = selectedRoleId ? roles.find(r => r.id === selectedRoleId) : null;
      
      const payload = {
        name: name.trim(),
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        username: editingTeamMember ? editingTeamMember.username : username?.trim(),
        roleId: selectedRoleId || null,
        roleName: selectedRole?.name || null,
        recurringAvailability: recurringAvailability || [],
        unavailableDates: unavailableDates || [],
        payRate: 20.0,
        schedulerWeight: schedulerWeight || 3,
      };

      if (password?.trim()) {
        payload.password = password.trim();
      } else if (!editingTeamMember) {
        toast.error("Password is required for new team members");
        setIsSaving(false);
        return;
      }

      if (editingTeamMember) {
        await orgApi.updateTeamMember(editingTeamMember.id, payload);
        toast.success("Team member updated successfully");
      } else {
        await orgApi.createTeamMember(payload);
        toast.success("Team member added successfully");
      }
      
      setFormHasChanges(false);
      setOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error("Error saving team member:", error);
      let errorMessage = "Failed to save team member";
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc?.[err.loc.length - 1] || 'Field'}: ${err.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        }
      }
      
      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (teamMember) => {
    setDeleteConfirm({ open: true, id: teamMember.id, name: teamMember.name });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await orgApi.deleteTeamMember(deleteConfirm.id);
      toast.success("Team member deleted successfully");
      setDeleteConfirm({ open: false, id: null, name: "" });
      onRefresh();
    } catch (error) {
      console.error("Error deleting team member:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete team member";
      
      if (error.response?.status === 400) {
        toast.error(errorMsg, { duration: 6000 });
      } else {
        toast.error("Failed to delete team member");
      }
      
      setDeleteConfirm({ open: false, id: null, name: "" });
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setEditingTeamMember(null);
    setName("");
    setEmail("");
    setPhone("");
    setUsername("");
    setPassword("");
    setRecurringAvailability([]);
    setUnavailableDates([]);
    setUsernameManuallyEdited(false);
    setSchedulerWeight(3);
    setSelectedRoleId("");
    setFormHasChanges(false);
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  // Sort team members — owner first, then alphabetically by name
  const sortedTeamMembers = [...(teamMembers || [])].sort((a, b) => {
    const aOwner = a.isOwnerMember || a.is_owner_member ? 1 : 0;
    const bOwner = b.isOwnerMember || b.is_owner_member ? 1 : 0;
    if (aOwner !== bOwner) return bOwner - aOwner;
    return (a.name || '').localeCompare(b.name || '');
  });

  // Pagination
  const totalPages = Math.ceil(sortedTeamMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTeamMembers = sortedTeamMembers.slice(startIndex, startIndex + itemsPerPage);

  // Error state
  if (propsError) {
    return (
      <TabPageLayout
        title="Team"
        subtitle="Manage your team members"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load team members</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => onRefresh()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Team"
      subtitle="Manage your team members"
      loading={propsLoading}
      actions={
        <div className="flex items-center gap-2">
          <LayoutActionButton
            icon={Plus}
            data-testid="add-team-member-button"
            onClick={() => setOpen(true)}
          >
            Add
          </LayoutActionButton>
        </div>
      }
    >
      <div data-testid="team-members-tab" className="space-y-6">
        <AddTeamMemberModal 
          isOpen={open && !editingTeamMember} 
          onClose={() => setOpen(false)} 
          onSuccess={onRefresh} 
        />
          
        {/* Edit Team Member Modal */}
        {editingTeamMember && (() => {
          const isOwner = !!(editingTeamMember?.isOwnerMember || editingTeamMember?.is_owner_member);
          const SettingsLink = ({ label = 'Change in Settings' }) => (
            <button
              type="button"
              onClick={goToProfileSettings}
              data-testid="owner-go-to-settings"
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              {label}
              <ArrowUpRight className="w-3 h-3" />
            </button>
          );
          return (
          <StandardModal
            isOpen={open}
            onClose={handleOpenChange}
            title={`Edit ${editingTeamMember.name}`}
            description={isOwner ? "The owner's details are managed in Settings → Profile" : "Update team member details"}
            size="md"
            footer={
              isOwner ? (
                <PrimaryButton onClick={() => handleOpenChange(false)} className="flex-1 sm:flex-none">
                  Done
                </PrimaryButton>
              ) : (
              <>
                <SecondaryButton onClick={() => handleOpenChange(false)} className="flex-1 sm:flex-none" disabled={isSaving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleSubmit} className="flex-1 sm:flex-none" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </PrimaryButton>
              </>
              )
            }
          >
            <div className="space-y-5">
              <ModalTabSelector
                tabs={[
                  { value: 'basic', label: 'Basic', icon: User },
                  { value: 'role', label: 'Role', icon: Users },
                ]}
                value={editTab}
                onChange={setEditTab}
              />

              {/* Tab 1: Basic Info */}
              {editTab === 'basic' && (
              <div className="space-y-4">
                {isOwner && (
                  <div className="flex items-start gap-2.5 rounded-xl bg-gray-50 px-3.5 py-3 text-xs text-gray-500">
                    <Shield className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>You're the organization owner. Your profile is managed in <strong className="font-medium text-gray-700">Settings → Profile</strong> to keep everything in sync.</span>
                  </div>
                )}
                <div>
                  <Label htmlFor="team-member-name">Name {!isOwner && <span className="text-red-500">*</span>}</Label>
                  <Input
                    id="team-member-name"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    disabled={isOwner}
                    placeholder="Team member name"
                    className={`mt-1 ${isOwner ? 'bg-gray-100 cursor-not-allowed' : ''} ${!isOwner && !name.trim() && name !== '' ? 'border-red-300 focus:border-red-500' : ''}`}
                  />
                  {isOwner ? (
                    <SettingsLink />
                  ) : (!name.trim() && name !== '' && (
                    <p className="text-sm text-red-500 mt-1">Name is required</p>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="team-member-email">Email</Label>
                    <Input
                      id="team-member-email"
                      type="email"
                      value={email}
                      disabled={true}
                      placeholder="email@example.com"
                      className="mt-1 bg-gray-100 cursor-not-allowed"
                    />
                    {isOwner
                      ? <SettingsLink />
                      : <p className="text-xs text-gray-500 mt-1">Can't edit. Delete & recreate to change.</p>}
                  </div>
                  <div>
                    <Label htmlFor="team-member-phone">Phone</Label>
                    <Input
                      id="team-member-phone"
                      type="tel"
                      value={phone}
                      disabled={true}
                      placeholder="(555) 123-4567"
                      className="mt-1 bg-gray-100 cursor-not-allowed"
                    />
                    {isOwner
                      ? <SettingsLink />
                      : <p className="text-xs text-gray-500 mt-1">Can't edit. Delete & recreate to change.</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="team-member-username">
                      Username 
                      <span className="text-xs text-gray-500 ml-1">(cannot change)</span>
                    </Label>
                    <Input
                      id="team-member-username"
                      value={username}
                      disabled={true}
                      className="bg-gray-100 mt-1 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <Label htmlFor="team-member-password">
                      Password 
                      {!isOwner && <span className="text-xs text-gray-500 ml-1">(leave blank to keep)</span>}
                    </Label>
                    <Input
                      id="team-member-password"
                      type="password"
                      value={isOwner ? "" : password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isOwner}
                      placeholder={isOwner ? "••••••••" : "New password"}
                      className={`mt-1 ${isOwner ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {isOwner && <SettingsLink label="Change password in Settings" />}
                  </div>
                </div>
              </div>
              )}

              {/* Tab 2: Role */}
              {editTab === 'role' && (
              <div className="space-y-4">
                <div>
                  <Label>Role {!isOwner && <span className="text-red-500">*</span>}</Label>
                  {isOwner ? (
                    <>
                      <div className="mt-1 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-100 px-3 h-9 text-sm text-gray-600 cursor-not-allowed">
                        <Shield className="w-4 h-4 text-blue-500" />
                        Owner — full access
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">The owner always has complete access to the dashboard. This can't be changed.</p>
                    </>
                  ) : (
                    <RoleSelector
                      roles={roles}
                      value={selectedRoleId}
                      onChange={setSelectedRoleId}
                      placeholder="Select a role..."
                      className="mt-1"
                    />
                  )}
                </div>
              </div>
              )}
            </div>
          </StandardModal>
          );
        })()}
        
        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedTeamMembers.length}
            onSelectAll={selectAllTeamMembers}
            onDeselectAll={deselectAllTeamMembers}
            onDelete={() => setBulkDeleteConfirm(true)}
            itemName="team member"
          />
        )}

        {/* Content */}
        {!teamMembers || teamMembers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No Team Members Added Yet"
            description="Add your first team member to get started"
            actionLabel="Add Team Member"
            onAction={() => setOpen(true)}
          />
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedTeamMembers.map((teamMember) => {
              const isSelected = selectedTeamMembers.includes(teamMember.id);
              const roleColor = roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6';
              const isOwner = teamMember.isOwnerMember || teamMember.is_owner_member;

              return (
                <ResourceCard
                  key={teamMember.id}
                  id={teamMember.id}
                  name={teamMember.name}
                  icon={User}
                  iconColor={roleColor}
                  description={undefined}
                  descriptionLines={[
                    ...(teamMember.email ? [{ icon: <Mail className="w-4 h-4" />, text: teamMember.email }] : []),
                    ...(teamMember.phone ? [{ icon: <Phone className="w-4 h-4" />, text: teamMember.phone }] : []),
                  ]}
                  topRightBadge={isOwner ? { label: 'Owner', color: '#2563EB' } : (teamMember.roleName ? { label: teamMember.roleName, color: roleColor } : null)}
                  showSeeMore={false}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={() => toggleTeamMemberSelection(teamMember.id)}
                  onEdit={() => handleEditClick(teamMember)}
                  onDelete={isOwner ? undefined : () => handleDeleteClick(teamMember)}
                />
              );
            })}
          </div>
        ) : (
          <div className="card-soft overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead className="bg-gray-50/60 border-b border-gray-100">
                  <tr>
                    {massActionMode && (
                      <th className="w-12 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedTeamMembers.length === paginatedTeamMembers.length && paginatedTeamMembers.length > 0}
                          onChange={(e) => e.target.checked ? selectAllTeamMembers() : deselectAllTeamMembers()}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedTeamMembers.map((teamMember) => {
                    const isSelected = selectedTeamMembers.includes(teamMember.id);
                    const isOwner = teamMember.isOwnerMember || teamMember.is_owner_member;
                    return (
                      <tr key={teamMember.id} className={`${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}>
                        {massActionMode && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTeamMemberSelection(teamMember.id)}
                              className="w-4 h-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{teamMember.name}</span>
                            {isOwner && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">Owner</span>
                            )}
                          </div>
                          {(teamMember.email || teamMember.phone) && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {teamMember.phone && <span className="mr-2">{teamMember.phone}</span>}
                              {teamMember.email && <span className="truncate">{teamMember.email}</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {teamMember.roleName ? (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6'}14`, color: roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6' }}
                            >
                              {teamMember.roleName}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No role</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(teamMember)}
                              data-testid={`edit-team-member-${teamMember.id}`}
                              className="text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            {!isOwner && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(teamMember)}
                                data-testid={`delete-team-member-${teamMember.id}`}
                                className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                title="Delete member"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {teamMembers && sortedTeamMembers.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={sortedTeamMembers.length}
          />
        )}

        {/* Delete Team Member Modal */}
        <DeleteConfirmModal
          isOpen={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, id: null, name: "" })}
          onConfirm={handleDeleteConfirm}
          title="Delete Team Member?"
          description={`Are you sure you want to delete ${deleteConfirm.name}?`}
          isDeleting={isDeleting}
        >
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>This team member will be unassigned from all future trips.</p>
        </DeleteConfirmModal>

        {/* Bulk Delete Modal */}
        <DeleteConfirmModal
          isOpen={bulkDeleteConfirm}
          onClose={() => setBulkDeleteConfirm(false)}
          onConfirm={handleBulkDelete}
          title="Delete Multiple Team Members?"
          description={`Are you sure you want to delete ${selectedTeamMembers.length} team member(s)?`}
          isDeleting={isBulkDeleting}
          confirmLabel={`Delete ${selectedTeamMembers.length} Members`}
        >
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>All selected team members will be unassigned from future trips.</p>
        </DeleteConfirmModal>
      </div>
    </TabPageLayout>
  );
};

export default TeamMembersTab;
